import React, { useRef, useState, useEffect, useCallback } from "react";
import {
  AspectRatio,
  Box,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  useDisclosure,
  type FlexProps,
} from "@chakra-ui/react";
import { VideoJsonLd } from "next-seo";
import { trackVideoEnded, trackVideoPlayed } from "components/event-tracking";
import { VideoPlayIcon } from "ui/icons";
import { type Theme } from "contexts/color-theme";

export type VideoModalProps = {
  /**
   * Whether to show the play button.
   * @default true
   *
   * Only omit if play & CTAs are handled externally.
   */
  hasPlayButton?: boolean;
  /**
   * Whether to autoplay the background video.
   * @default true
   */
  autoplayBgVideo?: boolean; // eslint-disable-line react/boolean-prop-naming
  /**
   * Determines if the background video is playing or not.
   * Used to play / pause the background video if `autoplayBgVideo` is false.
   * @default true
   */
  isBgVideoPlaying?: boolean;
  /** Shortened, separate video source to be used as b-roll for the card */
  shortSrc: string;
  /** The video source URL, should be an mp4 file */
  src: string;
  /** The path to the `.vtt` file containing video captions */
  captions?: string;
  /** The video title, used for SEO metadata */
  title: string;
  /** The video description, used for SEO metadata */
  description: string;
  /** The video upload date, used for SEO metadata */
  uploadDate: string;
  /** Video thumbnail image URL, used for SEO metadata */
  thumbnail: string;
  /** The video ID, used for tracking events */
  videoId: string;
  /**
   * The theme of the play icon
   * @default "light"
   */
  iconTheme?: Theme;
  /**
   * Determines the clickable space of the video.
   * @default "button"
   */
  clickableSpace?: "button" | "full";
  /**
   * Border radius for the video container.
   */
  borderRadius?: FlexProps["borderRadius"];
  /**
   * Whether the modal is open. If provided, controls the modal state externally.
   */
  isOpen?: boolean;
  /**
   * Callback to open the modal. If provided, controls the modal state externally.
   */
  onOpen?: () => void;
  /**
   * Callback to close the modal. If provided, controls the modal state externally.
   */
  onClose?: () => void;
};

export function VideoModal({
  shortSrc,
  src,
  captions,
  title,
  description,
  uploadDate,
  thumbnail,
  videoId,
  hasPlayButton = true,
  iconTheme = "light",
  autoplayBgVideo = true,
  isBgVideoPlaying = true,
  clickableSpace = "button",
  borderRadius,
  isOpen: externalIsOpen,
  onOpen: externalOnOpen,
  onClose: externalOnClose,
}: VideoModalProps) {
  const internalDisclosure = useDisclosure();
  const isOpen = externalIsOpen ?? internalDisclosure.isOpen;
  const onOpen = externalOnOpen ?? internalDisclosure.onOpen;
  const onClose = externalOnClose ?? internalDisclosure.onClose;
  const formattedDate = new Date(uploadDate).toISOString();
  const iconColor = iconTheme === "light" ? "white" : "brand.textSecondary.light";

  const bgVideo = useRef<HTMLVideoElement>(null);
  const [play, setPlay] = useState(false);

  // Play / Pause the background video depending on the `isBgVideoPlaying` prop.
  // Only run this when autoplayBgVideo is false.
  useEffect(() => {
    if (!autoplayBgVideo) {
      if (isBgVideoPlaying) {
        playBgVideo();
      } else {
        pauseBgVideo();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBgVideoPlaying, autoplayBgVideo]);

  const playBgVideo = useCallback(() => {
    const playPromise = bgVideo.current?.play();

    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          setPlay(true);
        })
        .catch(() => {
          setPlay(false);
        });
    }
  }, []);

  const pauseBgVideo = useCallback(() => {
    bgVideo.current?.pause();
    setPlay(false);
  }, []);

  return (
    <Box boxSize="100%">
      <Flex
        align="center"
        boxSize="100%"
        justify="center"
        pos="relative"
        borderRadius={borderRadius}
        overflow={borderRadius ? "hidden" : undefined}
      >
        <video
          ref={bgVideo}
          loop
          muted
          playsInline
          autoPlay={autoplayBgVideo}
          width="100%"
          height="100%"
          preload="metadata"
          poster={thumbnail}
        >
          <source src={shortSrc} type="video/mp4" />
        </video>

        {hasPlayButton && (
          <Flex
            as="button"
            aria-label="Open video modal and play video"
            align="center"
            borderRadius="full"
            border="1px solid"
            borderColor="transparent"
            color={iconColor}
            justify="center"
            cursor="pointer"
            pos="absolute"
            inset={clickableSpace === "full" ? 0 : undefined}
            w={clickableSpace === "full" ? "100%" : undefined}
            h={clickableSpace === "full" ? "100%" : undefined}
            opacity={0.6}
            overflow="hidden"
            outline="none"
            zIndex={10}
            transition="all 0.15s ease-in-out"
            _hover={{ opacity: 0.9 }}
            _focusVisible={{ opacity: 0.9, boxShadow: "outline" }}
            onClick={onOpen}
          >
            <VideoPlayIcon boxSize={{ base: "40px", sm: "60px" }} />
          </Flex>
        )}
      </Flex>

      <Modal isCentered size="6xl" isOpen={isOpen} onClose={onClose}>
        <ModalOverlay bg="blackAlpha.800" backdropFilter="blur(8px)" />
        <ModalContent m="small">
          <ModalBody p={0}>
            <AspectRatio ratio={16 / 9} w="100%" pos="relative">
              <video
                autoPlay
                controls
                playsInline
                width="100%"
                height="100%"
                preload="metadata"
                controlsList="nodownload"
                onPlay={() => {
                  trackVideoPlayed(videoId);
                }}
                onEnded={() => {
                  trackVideoEnded(videoId);
                }}
              >
                <source src={src} type="video/mp4" />
                {captions && <track kind="captions" srcLang="en-us" label="English" src={captions} />}
              </video>
            </AspectRatio>
          </ModalBody>
        </ModalContent>
      </Modal>

      <VideoJsonLd
        name={title}
        description={description}
        contentUrl={src}
        embedUrl={src}
        thumbnailUrls={[thumbnail]}
        uploadDate={formattedDate}
      />
    </Box>
  );
}
